/******************************************
*  Author : Riccardo Di Dato   
*  Created On : Mon May 30 2022
*  File : GA4StatsCollector.ts
*******************************************/

import { Logger } from "@ciaopeople/cp-shatsu/Logging";
import { GA4UnifiedHelper } from "@ciaopeople/cp-shatsu/Stats";
import { AuthProviderIdentifier } from "../login/AuthProviderIdentifier.js";
import { LoginMode } from "../login/LoginMode.js";
import { MembershipAnalyticsInfo, StatsCollector } from "./StatsCollector.js";
import { CTAStatsDetails, CTA_NAME, CTA_OUTCOME, SOURCE_ELEMENT } from "./CTATypes.js";
import { BookmarkAction, DeviceAction, NewlsetterAction, StatDeviceType, TopicAction } from "./UserActionTypes.js";
import { getRangeByAge } from "./AgeRange.js";
import { UserGender } from "@api-commons/api/types/UserDataTypes.js";
import { GA4EventProperties } from "@ciaopeople/cp-shatsu/Stats/GA4UnifiedHelper";

export class GA4StatsCollector implements StatsCollector {

    constructor(
        private readonly analytics: GA4UnifiedHelper,
        private logger?: Logger,
        maybeGeneralParameters?: GA4EventProperties
    ) {
       

        analytics.configure(maybeGeneralParameters);
    }


    markUserPersInfo(age: number, userGender: UserGender, memberInfo?: MembershipAnalyticsInfo): Promise<void> {
        // if (this.logger) {
        //     this.logger.log("markUserPersInfo age", age, "userGender", userGender);
        // }
        const props: { [k: string]: string } = {};
        if (memberInfo) {
            props.membership_type = memberInfo.membership_type;
            props.membership_status = memberInfo.membership_status ? (memberInfo.membership_is_trial?"trial":"active") : "not_active";
        }
        if(age > 0 ) {
            const ar = getRangeByAge(age);
            if(ar && ar.getLiteral() != "") {
                props.age_range = ar.getLiteral();
            }
        }
        if(userGender != UserGender.UNKNOWN) {
            props.user_gender = userGender == UserGender.MALE?"U":"D";
        }
        if(props.age_range || props.user_gender || props.membership_type) return this.analytics.setUserProperties(props);
        else return Promise.resolve();
    }

    public markLoginStatus(loggedIn: boolean, provider: AuthProviderIdentifier | "UNK"): Promise<void> {
        if (this.logger) {
            this.logger.log("markLoginStatus loggedIn", loggedIn, "provider", provider);
        }
        if(loggedIn && provider) {
            return this.analytics.setUserProperty("login_status", provider.toUpperCase() + "_LOGIN");
        }
        else return Promise.resolve();
    }

    public loginDone(provider: AuthProviderIdentifier| "UNK", mode: LoginMode, newUser: boolean): Promise<void> {
        if (this.logger) {
            this.logger.log("loginDone provider", provider, "mode", mode, "newUser", newUser);
        }
        const eventName = newUser ? "signup" : "login";
        return this.analytics.sendEvent( eventName, {
            provider: provider.toUpperCase(),
            login_mode: mode.toUpperCase(),
        } );
    }

    
    
    public logoutDone(provider: AuthProviderIdentifier| "UNK"): Promise<void> {
        if (this.logger) {
            this.logger.log("logoutDone provider", provider);
        }
        return this.analytics.sendEvent( "logout", {
            provider: provider.toUpperCase()
        } ).then(_x => {
            return this.analytics.setUserProperty("login_status", null);
        });
    }

    public collectCTAData(data: CTAStatsDetails): Promise<void> {
        const whitelisted_impression: CTA_NAME[] = 
            [
                CTA_NAME.EMAIL_FIX_REQUIRED_DIALOG,
                CTA_NAME.EMAIL_LOGIN_REQUIRED_DIALOG,
                CTA_NAME.MEMBERSHIP_REQUIRED_DIALOG,
                CTA_NAME.LOGIN_REQUIRED_DIALOG
            ];
        if (data.outcome != CTA_OUTCOME.SHOWN || whitelisted_impression.indexOf(data.action) >= 0 ) {
            if (this.logger) {
                this.logger.log("collectCTAData data", data);
            }
    
            const eventParams: Record <string, string | number> = {
                action: data.action,
                outcome: data.outcome,
                source_element: data.source_element
            };
    

            if(data.topic) eventParams.topic = data.topic;
            if(data.provider) eventParams.provider = data.provider;      
            if(data.device_type) eventParams.device_type = data.device_type;
            if(data.url) eventParams.url = data.url;
            if(data.membership_type) eventParams.membership_type = data.membership_type;

            return this.analytics.sendEvent( "cta", eventParams);
        }
        else {
            return Promise.resolve();
        }

    }


    


    public collectTopicActionData(source_element: SOURCE_ELEMENT, name: TopicAction | NewlsetterAction, url: string): Promise<void> {
        if (this.logger) {
            this.logger.log("collectTopicActionData data", name, url);
        }
        return this.analytics.sendEvent( "topic", {
            action: name.toLowerCase(),
            link_url: url,
            source_element: source_element
        } );
    }

    public collectDeviceActionData(name: DeviceAction, device: StatDeviceType): Promise<void> {
        if (this.logger) {
            this.logger.log("collectDeviceActionData data", name, device);
        }
        return this.analytics.sendEvent( "device", {
            action: name.toLowerCase(),
            device_type: device.toUpperCase()
        } );
    }

    public collectBookmarkActionData(source_element: SOURCE_ELEMENT, name: BookmarkAction, url: string): Promise<void> {
        if (this.logger) {
            this.logger.log("collectBookmarkActionData data", name, url);
        }
        return this.analytics.sendEvent( "bookmark", {
            action: name.toLowerCase(),
            link_url: url,
            source_element: source_element
        } );
    }

    public collectPageview(page: string): Promise<void> {
        if (this.logger) {
            this.logger.log("collectPageview data", page);
        }
        return this.analytics.sendPageView({}, "Pagina personale", page );
    }


    public freePageView(page_title?: string, page_location?: string, other_params?: Record<string,any>): Promise<void> {
        if(this.logger) {
            this.logger.log("freePageView",page_title,page_location,other_params);
        }
        const uparams = other_params?other_params:{};
        return this.analytics.sendPageView(uparams,page_title,page_location);
    }

    public freeEvent(event_name: string, params?: Record<string,any>): void {
        const uparams = params?params:{};
        this.analytics.fireAndForgetEvent(event_name,uparams);
    }


    iosNotificationOpen(): Promise<void> {
        if (this.logger) {
            this.logger.log("iosNotificationOpen");
        }
        return this.analytics.sendEvent( "notification_open", {});
    }


    collectBuyMembershipButtonClick(loginRequired: boolean): Promise<void> {
        return this.analytics.sendEvent( "click", { source_element: "buy_membership", element_section: loginRequired ? "login_required" : "login_not_required" });
    }

    collectLoginDialogButtonPress(sourceSection: AuthProviderIdentifier ): Promise<void> {
        return this.analytics.sendEvent( "click", { source_element: "buy_membership", element_section: sourceSection, provider: sourceSection });
    }
}